import axios, { type AxiosInstance, type AxiosResponse, type AxiosError } from 'axios'
import { toast } from 'vue-sonner'
import type {
    ApiResponse,
    LoginResponse,
    MeResponse,
    User,
    UserWithStats,
    Team,
    Matchday,
    MatchdayWithPrediction,
    Prediction,
    StandingsEntry,
    StandingsHistory,
    UserSummary,
    AdminStats,
    ProcessResult,
    AppConfig,
    PaginatedResponse,
    AutomationStatus,
    AutomationLog,
    ScraperHealth,
    ImportResult
} from '@/types/api'

// =============================================================================
// Configuration
// =============================================================================

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1'

// =============================================================================
// API Client Factory
// =============================================================================

function createApiClient(): AxiosInstance {
    const api = axios.create({
        baseURL: API_BASE_URL,
        timeout: 10000,
        headers: { 'Content-Type': 'application/json' }
    })

    // Request interceptor - add auth token
    api.interceptors.request.use((config) => {
        const session = JSON.parse(localStorage.getItem('user') || '{}')
        if (session?.accessToken) {
            config.headers.Authorization = `Bearer ${session.accessToken}`
        }
        return config
    })

    // Response interceptor - handle errors
    api.interceptors.response.use(
        (response) => response,
        (error: AxiosError<{ error: string }>) => {
            const status = error.response?.status
            const message = error.response?.data?.error || error.message

            // Transient failures are reported with a toast, not a blocking
            // modal: a rate limit or a hiccup does not warrant stopping the
            // page and making the user dismiss a dialog. The strings are
            // Spanish, like the rest of the interface.
            if (status === 401) {
                // Not every 401 means the session died: a wrong current
                // password on the change form answers 401 too, and dumping
                // the user at the login screen for a typo reads as data loss.
                const url = error.config?.url || ''
                if (!url.includes('auth/password')) {
                    localStorage.removeItem('user')
                    window.location.href = '/login'
                }
            } else if (status === 403) {
                // Endpoints that present their own 403 inline opt out here.
                const url = error.config?.url || ''
                if (!url.includes('/recap')) {
                    toast.error('Acceso denegado', {
                        description: message || 'No tienes permiso para hacer esto.'
                    })
                }
            } else if (status === 429) {
                const retryAfter = (error.response?.data as { retryAfter?: number })?.retryAfter
                toast.warning('Demasiadas peticiones', {
                    description: retryAfter
                        ? `Espera ${Math.ceil(retryAfter / 60)} min e inténtalo de nuevo.`
                        : 'Espera un momento e inténtalo de nuevo.'
                })
            } else if (status && status >= 500) {
                toast.error('Error del servidor', {
                    description: 'Algo ha fallado. Inténtalo de nuevo más tarde.'
                })
            }

            return Promise.reject(error)
        }
    )

    return api
}

const api = createApiClient()

// =============================================================================
// useApi Composable
// =============================================================================

export function useApi() {
    const get = async <T>(endpoint: string, params?: Record<string, unknown>): Promise<AxiosResponse<T>> => {
        return api.get<T>(endpoint, { params })
    }

    const post = async <T>(endpoint: string, data?: Record<string, unknown>): Promise<AxiosResponse<T>> => {
        return api.post<T>(endpoint, data)
    }

    const put = async <T>(endpoint: string, data?: Record<string, unknown>): Promise<AxiosResponse<T>> => {
        return api.put<T>(endpoint, data)
    }

    const del = async <T>(endpoint: string): Promise<AxiosResponse<T>> => {
        return api.delete<T>(endpoint)
    }

    // ==========================================================================
    // Auth Endpoints
    // ==========================================================================

    const login = (email: string, password: string) =>
        post<LoginResponse>('/auth/login', { email, password })

    const refresh = () => post<{ token: string }>('/auth/refresh')

    const getMe = () => get<MeResponse>('/auth/me')

    const updateProfile = (data: { name?: string; email?: string }) =>
        put<{ data: { id: number; email: string; name: string | null; role: string } }>('/auth/me', data)

    const changePassword = (currentPassword: string, newPassword: string) =>
        put<{ message: string }>('/auth/password', { currentPassword, newPassword })

    const getNotificationPreferences = () =>
        get<{ data: { resultsEmail: boolean; reminderEmail: boolean } }>('/auth/preferences')

    const updateNotificationPreferences = (prefs: { resultsEmail?: boolean; reminderEmail?: boolean }) =>
        api.patch<{ data: { resultsEmail: boolean; reminderEmail: boolean } }>('/auth/preferences', prefs)

    // ==========================================================================
    // Teams Endpoints
    // ==========================================================================

    const getTeams = (season?: string) =>
        get<ApiResponse<Team[]>>('/teams', season ? { season } : undefined)

    const getTeam = (id: number) =>
        get<ApiResponse<Team>>(`/teams/${id}`)

    // ==========================================================================
    // Matchdays Endpoints
    // ==========================================================================

    const getMatchdays = (options?: { season?: string; withPredictions?: boolean }) =>
        get<ApiResponse<MatchdayWithPrediction[]>>('/matchdays', {
            season: options?.season,
            withPredictions: options?.withPredictions ? 'true' : undefined
        })

    const getMatchday = (number: number, season?: string) =>
        get<{ data: Matchday; prediction: Prediction | null }>(`/matchdays/${number}`, season ? { season } : undefined)

    const getCurrentMatchday = (season?: string) =>
        get<{ data: Matchday; prediction: Prediction | null }>('/matchdays/current', season ? { season } : undefined)

    const getMatchdayRecap = (number: number, season?: string) =>
        get<{ data: { leaderboard: unknown[]; predictionMatrix: unknown[] } }>(`/matchdays/${number}/recap`, season ? { season } : undefined)

    // ==========================================================================
    // Predictions Endpoints
    // ==========================================================================

    const getPredictions = (options?: { season?: string; matchday?: number }) =>
        get<ApiResponse<Prediction[]>>('/predictions', options)

    const getPrediction = (id: number) =>
        get<ApiResponse<Prediction>>(`/predictions/${id}`)

    const createPrediction = (matchdayId: number, predictions: Array<{ matchNumber: number; homeScore: number; awayScore: number }>) =>
        post<ApiResponse<Prediction>>('/predictions', { matchdayId, predictions })

    const updatePrediction = (id: number, predictions: Array<{ matchNumber: number; homeScore: number; awayScore: number }>) =>
        put<ApiResponse<Prediction>>(`/predictions/${id}`, { predictions })

    // ==========================================================================
    // Standings Endpoints
    // ==========================================================================

    const getStandings = (season?: string) =>
        get<{ data: StandingsEntry[]; matchday: number | null }>('/standings', season ? { season } : undefined)

    const getStandingsHistory = (userId: number, season?: string) =>
        get<ApiResponse<StandingsHistory[]>>(`/standings/history/${userId}`, season ? { season } : undefined)

    const getSummary = (season?: string) =>
        get<ApiResponse<UserSummary>>('/standings/summary', season ? { season } : undefined)

    const getHeadToHead = <T = unknown>(targetUserId: number, season?: string) =>
        get<{ data: T }>(`/standings/compare/${targetUserId}`, season ? { season } : undefined)

    return {
        // Raw methods
        get,
        post,
        put,
        delete: del,

        // Auth
        login,
        refresh,
        getMe,
        updateProfile,
        changePassword,
        getNotificationPreferences,
        updateNotificationPreferences,

        // Teams
        getTeams,
        getTeam,

        // Matchdays
        getMatchdays,
        getMatchday,
        getCurrentMatchday,
        getMatchdayRecap,

        // Predictions
        getPredictions,
        getPrediction,
        createPrediction,
        updatePrediction,

        // Standings
        getStandings,
        getStandingsHistory,
        getSummary,
        getHeadToHead
    }
}

// =============================================================================
// useAdminApi Composable
// =============================================================================

export function useAdminApi() {
    // ==========================================================================
    // Users
    // ==========================================================================

    const getUsers = (options?: { page?: number; limit?: number; search?: string }) =>
        api.get<PaginatedResponse<UserWithStats>>('/admin/users', { params: options })

    const getUser = (id: number) =>
        api.get<ApiResponse<UserWithStats>>(`/admin/users/${id}`)

    const updateUser = (id: number, data: { name?: string; email?: string; role?: string; password?: string }) =>
        api.put<ApiResponse<User>>(`/admin/users/${id}`, data)

    const deleteUser = (id: number) =>
        api.delete<{ success: boolean }>(`/admin/users/${id}`)

    const inviteUser = (data: { email: string; name?: string }) =>
        api.post<ApiResponse<User>>('/admin/users/invite', data)

    // ==========================================================================
    // Teams
    // ==========================================================================

    const getAdminTeams = (season?: string) =>
        api.get<ApiResponse<Team[]>>('/admin/teams', { params: { season } })

    const createTeam = (data: { name: string; shortName: string; logo?: string | null; season?: string }) =>
        api.post<ApiResponse<Team>>('/admin/teams', data)

    const updateTeam = (id: number, data: { name?: string; shortName?: string; logo?: string | null; season?: string }) =>
        api.put<ApiResponse<Team>>(`/admin/teams/${id}`, data)

    const deleteTeam = (id: number) =>
        api.delete<{ success: boolean }>(`/admin/teams/${id}`)

    // ==========================================================================
    // Matchdays
    // ==========================================================================

    const getMatchdays = (season?: string) =>
        api.get<ApiResponse<Matchday[]>>('/matchdays', { params: { season } })

    const createMatchday = (data: {
        matchdayNumber: number
        season: string
        deadline?: string
        matches: Array<{ matchNumber: number; homeTeamId: number; awayTeamId: number }>
    }) =>
        api.post<ApiResponse<Matchday>>('/admin/matchdays', data)

    const updateMatchday = (id: number, data: { deadline?: string; status?: string; matches?: unknown[] }) =>
        api.put<ApiResponse<Matchday>>(`/admin/matchdays/${id}`, data)

    const updateMatchResults = (id: number, matches: Array<{ matchNumber: number; homeScore: number; awayScore: number; played?: boolean }>) =>
        api.put<ApiResponse<Matchday>>(`/admin/matchdays/${id}/results`, { matches })

    const deleteMatchday = (id: number) =>
        api.delete<{ success: boolean }>(`/admin/matchdays/${id}`)

    const processMatchday = (id: number) =>
        api.post<{ success: boolean; data: ProcessResult }>(`/admin/process-matchday/${id}`)

    const reprocessMatchday = (id: number) =>
        api.post<{ success: boolean; data: ProcessResult }>(`/admin/reprocess-matchday/${id}`)

    // ==========================================================================
    // Config
    // ==========================================================================

    const getConfig = () =>
        api.get<ApiResponse<AppConfig>>('/admin/config')

    const updateConfig = (data: Partial<AppConfig>) =>
        api.put<{ success: boolean }>('/admin/config', data)

    // ==========================================================================
    // Stats
    // ==========================================================================

    const getStats = (season?: string) =>
        api.get<ApiResponse<AdminStats>>('/admin/stats', { params: { season } })

    // ==========================================================================
    // Automation
    //
    // The scheduler that scrapes results, locks rounds and scores them. The
    // panel reads its status and can nudge each step by hand when a scrape
    // fails, which is the whole reason these are exposed.
    // ==========================================================================

    const getAutomationStatus = (season?: string) =>
        api.get<ApiResponse<AutomationStatus>>('/admin/automation/status', { params: { season } })

    const getAutomationLogs = (options?: { limit?: number; severity?: string; season?: string }) =>
        api.get<ApiResponse<AutomationLog[]>>('/admin/automation/logs', { params: options })

    const getAutomationErrors = (hours = 24) =>
        api.get<ApiResponse<AutomationLog[]>>('/admin/automation/errors', { params: { hours } })

    const getScraperHealth = () =>
        api.get<ApiResponse<ScraperHealth>>('/admin/automation/scraper-health')

    const resetScraperHealth = () =>
        api.post<{ success: boolean }>('/admin/automation/scraper-health/reset')

    const startAutomation = () => api.post<{ success: boolean }>('/admin/automation/start')

    const stopAutomation = () => api.post<{ success: boolean }>('/admin/automation/stop')

    const triggerAutomation = (job?: string) =>
        api.post<{ success: boolean }>('/admin/automation/trigger', job ? { job } : {})

    const importFixtures = (season: string) =>
        api.post<{ success: boolean; data: ImportResult }>('/admin/automation/import-fixtures', {
            season
        })

    const verifyImport = (season?: string) =>
        api.get<ApiResponse<Record<string, unknown>>>('/admin/automation/verify-import', {
            params: { season }
        })

    const fetchResults = (matchdayNumber: number) =>
        api.post<{ success: boolean; data: unknown }>(
            `/admin/automation/fetch-results/${matchdayNumber}`
        )

    const syncMatchTimes = (season?: string) =>
        api.post<{ success: boolean; data: unknown }>('/admin/automation/sync-match-times', {
            season
        })

    const lockMatchday = (id: number) =>
        api.post<{ success: boolean }>(`/admin/automation/lock-matchday/${id}`)

    return {
        // Users
        getUsers,
        getUser,
        updateUser,
        deleteUser,
        inviteUser,

        // Teams
        getAdminTeams,
        createTeam,
        updateTeam,
        deleteTeam,

        // Matchdays
        getMatchdays,
        createMatchday,
        updateMatchday,
        updateMatchResults,
        deleteMatchday,
        processMatchday,
        reprocessMatchday,

        // Config
        getConfig,
        updateConfig,

        // Stats
        getStats,

        // Automation
        getAutomationStatus,
        getAutomationLogs,
        getAutomationErrors,
        getScraperHealth,
        resetScraperHealth,
        startAutomation,
        stopAutomation,
        triggerAutomation,
        importFixtures,
        verifyImport,
        fetchResults,
        syncMatchTimes,
        lockMatchday
    }
}
